import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ICON } from '../icons/iconIds';
import SelkirkIcon from './icons/SelkirkIcon';
import Layout from './layout/Layout';
import Card from './ui/Card';
import Button from './ui/Button';
import Spinner from './ui/Spinner';
import { labApprovalsService } from '../services/labApprovalsService';
import { docusignService } from '../services/docusignService';
import { procurifyLabService } from '../services/procurifyLabService';
import LabApprovalsProcurifyPanel from './LabApprovalsProcurifyPanel';
import LabApprovalsProcurifyBillPanel from './LabApprovalsProcurifyBillPanel';
import Tabs from './ui/Tabs';
import './LabApprovals.css';

const displayNameFromUser = (u) => {
  if (!u) return '';
  const name = (u.name || '').trim();
  if (name) return name;
  return (u.username || u.email || '').trim();
};

/**
 * QAQC / data entry often prefixes notes with "C1". Strip that (and following whitespace)
 * so approval comments don't show "C1" ahead of the approval stamp.
 */
const stripLeadingC1Prefix = (text) => {
  let s = String(text ?? '').trim();
  if (/^C1\s*$/i.test(s)) return '';
  s = s.replace(/^\s*C1\b\s*/i, '').trim();
  return s;
};

/** Best-effort sample count for approval stamp (sequential leach vs standard). */
const effectiveSampleCountForStamp = (formFields, row) => {
  const parse = (v) => {
    if (v === '' || v == null) return null;
    const n = Number(String(v).replace(/,/g, ''));
    return Number.isFinite(n) && n >= 0 ? n : null;
  };
  const shipped = parse(formFields?.samples_sent_shipped);
  if (shipped != null && shipped > 0) return shipped;
  const analysis = parse(formFields?.sample_num_analysis);
  if (analysis != null && analysis > 0) return analysis;
  const prep = parse(formFields?.sample_num_prep);
  if (prep != null && prep > 0) return prep;
  if (row?.sample_count != null && Number(row.sample_count) > 0) {
    return Number(row.sample_count);
  }
  const pivot = row?.samples_sent_shipped_from_results;
  if (pivot != null && Number.isFinite(Number(pivot)) && Number(pivot) > 0) {
    return Number(pivot);
  }
  return null;
};

/**
 * When the review modal opens: set comments to an approval stamp (signed-in user).
 * If sample count is known and is under 20, uses Sequential Leach wording.
 */
const appendApprovedByStamp = (formFields, user, rowForSamples = null) => {
  const who = displayNameFromUser(user);
  if (!who || !formFields) return;

  const n = effectiveSampleCountForStamp(formFields, rowForSamples);
  const useSequentialLeach = n != null && n < 20;
  const stamp = useSequentialLeach
    ? `Sequential Leach - Approved By ${who}`
    : `Approved by ${who}`;

  let cur = stripLeadingC1Prefix(formFields.comments || '');
  if (!cur) {
    formFields.comments = stamp;
    return;
  }
  if (cur.includes(stamp)) {
    formFields.comments = cur;
    return;
  }
  formFields.comments = `${cur}\n\n${stamp}`;
};

const emptyForm = () => ({
  workorder_id: '',
  po_number: '',
  sample_num_prep: '',
  sample_num_analysis: '',
  samples_sent_shipped: '',
  subtotal: '',
  gst: '',
  total: '',
  approved: '',
  approval_date: '',
  comments: '',
  average_sample_cost: '',
  folio_id: '',
  software_source: '',
  procurify_bill_id: '',
});

/** Procurify bill uuids are 32-char hex (no dashes); DocuSign envelope ids are dashed GUIDs. */
const isProcurifyFolioId = (folioId) => {
  const s = String(folioId || '').trim();
  if (!s) return false;
  const compact = s.replace(/-/g, '');
  return compact.length === 32 && /^[0-9a-f]{32}$/i.test(compact);
};

const formUsesProcurifySource = (f, docSource) => {
  const form = f || {};
  if (form.software_source === 'procurify') return true;
  if (docSource === 'procurify') return true;
  if (String(form.procurify_bill_id || '').trim()) return true;
  if (!form.software_source && isProcurifyFolioId(form.folio_id)) return true;
  return false;
};

/** Round currency-like average to 2 decimal places for display / save. */
const roundAverageSampleCostStr = (raw) => {
  const s = String(raw ?? '').trim();
  if (s === '') return '';
  const x = Number(s.replace(/,/g, ''));
  if (!Number.isFinite(x)) return s;
  return x.toFixed(2);
};

const formFromApproval = (approval) => {
  const a = approval || {};
  const n = (v) => (v == null || v === '' ? '' : String(v));
  const approvedVal =
    a.approved === true ? 'true' : a.approved === false ? 'false' : '';
  return {
    po_number: a.po_number != null ? String(a.po_number) : '',
    sample_num_prep: n(a.sample_num_prep),
    sample_num_analysis: n(a.sample_num_analysis),
    samples_sent_shipped: n(a.samples_sent_shipped),
    subtotal: n(a.subtotal),
    gst: n(a.gst),
    total: n(a.total),
    approved: approvedVal,
    approval_date: a.approval_date ? String(a.approval_date).slice(0, 10) : '',
    comments: a.comments != null ? String(a.comments) : '',
    average_sample_cost: roundAverageSampleCostStr(a.average_sample_cost),
    folio_id: a.folio_id != null ? String(a.folio_id).trim() : '',
    software_source: a.software_source != null ? String(a.software_source).trim() : '',
    procurify_bill_id:
      a.procurify_bill_id != null && a.procurify_bill_id !== ''
        ? String(a.procurify_bill_id)
        : '',
  };
};

/** Map API extraction result into approval form state (same shape as list-driven modal). */
/** Compact list row; full list in title / tooltip */
const formatHoleIdsShort = (ids) => {
  if (!ids || !ids.length) return null;
  if (ids.length <= 2) return ids.join(', ');
  return `${ids.slice(0, 2).join(', ')} +${ids.length - 2}`;
};

const formatNewsDatesShort = (dates) => {
  if (!dates || !dates.length) return null;
  if (dates.length === 1) return dates[0];
  return `${dates[0]} (+${dates.length - 1})`;
};

const formFromExtracted = (data) => {
  const d = data || {};
  const n = (v) => (v == null || v === '' ? '' : String(v));
  const approvedVal =
    d.approved === true ? 'true' : d.approved === false ? 'false' : '';
  let avgCost = n(d.average_sample_cost);
  if (avgCost !== '') {
    avgCost = roundAverageSampleCostStr(avgCost);
  }
  if (avgCost === '' && d.total != null && d.total !== '' && d.sample_num_analysis != null) {
    const t = Number(d.total);
    const sn = Number(d.sample_num_analysis);
    if (sn > 0 && Number.isFinite(t)) {
      avgCost = (t / sn).toFixed(2);
    }
  }
  return {
    workorder_id: d.workorder_id != null ? String(d.workorder_id).trim() : '',
    po_number: d.po_number != null ? String(d.po_number) : '',
    sample_num_prep: n(d.sample_num_prep),
    sample_num_analysis: n(d.sample_num_analysis),
    samples_sent_shipped: n(d.samples_sent_shipped),
    subtotal: n(d.subtotal),
    gst: n(d.gst),
    total: n(d.total),
    approved: approvedVal,
    approval_date: d.approval_date ? String(d.approval_date).slice(0, 10) : '',
    comments: d.comments != null ? String(d.comments) : '',
    average_sample_cost: avgCost,
    folio_id: d.folio_id != null ? String(d.folio_id).trim() : '',
    software_source: d.software_source != null ? String(d.software_source).trim() : '',
    procurify_bill_id:
      d.procurify_bill_id != null && d.procurify_bill_id !== ''
        ? String(d.procurify_bill_id)
        : '',
  };
};

/** QAQC + approval workflow for list + modal chrome. */
const getLabApprovalFlowState = (qaqcPassed, isApproved) => {
  if (qaqcPassed !== true) return 'failure';
  if (isApproved === true) return 'approved';
  return 'ready';
};

const LAB_FLOW_META = {
  failure: {
    label: 'Failure',
    title: 'QAQC not passed or unavailable — resolve before approval',
  },
  ready: {
    label: 'Ready for Approval',
    title: 'QAQC passed — pending approval',
  },
  approved: {
    label: 'Approved',
    title: 'QAQC passed and workorder approved',
  },
};

/** folio (envelope id) → Map(lower workorder_id → dashboard row). */
const buildApprovalsByFolioWorkorder = (approvalsData) => {
  const outer = new Map();
  for (const item of approvalsData || []) {
    const fid =
      item.approval?.folio_id != null
        ? String(item.approval.folio_id).trim().toLowerCase()
        : '';
    if (!fid) continue;
    const wid =
      item.workorder_id != null ? String(item.workorder_id).trim().toLowerCase() : '';
    if (!wid) continue;
    if (!outer.has(fid)) outer.set(fid, new Map());
    outer.get(fid).set(wid, item);
  }
  return outer;
};

const segmentLinkedApproval = (byFolioWorkorder, envelopeId, hintWorkorderId) => {
  const fid = (envelopeId || '').trim().toLowerCase();
  const wid = (hintWorkorderId || '').trim().toLowerCase();
  if (!fid || !wid) return null;
  return byFolioWorkorder.get(fid)?.get(wid) ?? null;
};

/** Picker row: match by linked/hint workorder id + folio, then workorder id alone. */
const resolveSegmentDashboardRow = (seg, envelopeId, byFolioWorkorder, approvalsData) => {
  const fid = (envelopeId || '').trim().toLowerCase();
  if (!fid || !seg) return null;

  const guessed = workorderIdGuessFromSegment(seg);
  const idCandidates = [seg.linkedWorkorder_id, seg.hint_workorder_id, guessed]
    .map((x) => (x != null ? String(x).trim() : ''))
    .filter(Boolean);

  for (const raw of idCandidates) {
    const wid = raw.toLowerCase();
    const fromFolio = byFolioWorkorder.get(fid)?.get(wid);
    if (fromFolio) return fromFolio;

    const row = (approvalsData || []).find(
      (w) => String(w.workorder_id || '').trim().toLowerCase() === wid
    );
    if (!row) continue;
    const rowFolio = String(row.approval?.folio_id || '').trim().toLowerCase();
    if (!rowFolio || rowFolio === fid) return row;
    if (row.approval) return row;
  }
  return null;
};

const mergeSavedWorkorderIntoApprovalsData = (prev, payload, ctxRow, approvedFlag) => {
  const wid = String(payload.workorder_id || '').trim();
  if (!wid) return prev;
  const norm = wid.toLowerCase();
  const folio = payload.folio_id != null ? String(payload.folio_id).trim() : null;

  const approvalFields = {
    workorder_id: wid,
    folio_id: folio,
    po_number: payload.po_number ?? null,
    sample_num_prep: payload.sample_num_prep ?? null,
    sample_num_analysis: payload.sample_num_analysis ?? null,
    samples_sent_shipped: payload.samples_sent_shipped ?? null,
    subtotal: payload.subtotal ?? null,
    gst: payload.gst ?? null,
    total: payload.total ?? null,
    approval_date: payload.approval_date ?? null,
    comments: payload.comments ?? null,
    average_sample_cost: payload.average_sample_cost ?? null,
  };
  if (approvedFlag === true) {
    approvalFields.approved = true;
  } else if (approvedFlag === false) {
    approvalFields.approved = false;
  }

  const idx = prev.findIndex(
    (w) => String(w.workorder_id || '').trim().toLowerCase() === norm
  );

  if (idx >= 0) {
    const row = prev[idx];
    const mergedApproval = { ...(row.approval || {}), ...approvalFields };
    if (approvedFlag === true) mergedApproval.approved = true;
    const next = [...prev];
    next[idx] = {
      ...row,
      approval: mergedApproval,
      status: approvedFlag === true ? 'approved' : row.status,
    };
    return next;
  }

  const stub = ctxRow && String(ctxRow.workorder_id || '').trim().toLowerCase() === norm
    ? { ...ctxRow }
    : {
        workorder_id: wid,
        qaqc_passed: null,
        sample_count: null,
        samples_sent_shipped_from_results: null,
        lab_name: null,
        hole_ids: [],
        news_release_dates: [],
        qaqc_workorder_note: null,
      };

  return [
    {
      ...stub,
      workorder_id: wid,
      status: approvedFlag === true ? 'approved' : stub.status || 'pending',
      approval: {
        ...approvalFields,
        approved: approvedFlag === true ? true : null,
      },
    },
    ...prev,
  ];
};

/** Badge for a workorder row linked by folio_id (DocuSign envelope id). */
const linkedWorkorderBadge = (item) => {
  if (!item) return null;
  const wid = item.workorder_id != null ? String(item.workorder_id).trim() : '';
  if (item.approval?.approved === false) {
    return {
      key: 'rejected',
      label: 'Rejected',
      title: wid
        ? `Workorder ${wid}: lab approval rejected`
        : 'Lab approval rejected',
      wid,
    };
  }
  const flow = getLabApprovalFlowState(item.qaqc_passed, item.approval?.approved === true);
  return {
    key: flow,
    label: LAB_FLOW_META[flow].label,
    title: wid ? `${wid} — ${LAB_FLOW_META[flow].title}` : LAB_FLOW_META[flow].title,
    wid,
  };
};

const LabApprovalFlowIcon = ({ state, className = '' }) => {
  const meta = LAB_FLOW_META[state] || LAB_FLOW_META.failure;
  return (
    <span
      className={`lab-approvals-flow-icon lab-approvals-flow-icon--${state} ${className}`.trim()}
      title={meta.title}
      role="img"
      aria-label={meta.label}
    >
      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
        <circle
          cx="12"
          cy="12"
          r="8"
          fill="currentColor"
          fillOpacity="0.2"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    </span>
  );
};

/** Prefill samples_sent_shipped from list row: SM (sample_count) first, then Shp (results pivot count). */
/** Rows from DocuSign search_folders / list envelopes API (shape varies). */
const docusignAwaitingRowsFromApiData = (data) => {
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data.envelopeSummaries)) return data.envelopeSummaries;
  if (Array.isArray(data.folderItems)) return data.folderItems;
  if (Array.isArray(data.envelopes)) return data.envelopes;
  return [];
};

const segmentDisplayLabel = (seg, index) => {
  const linked = (seg?.linkedWorkorder_id || '').trim();
  const hint = (seg?.hint_workorder_id || '').trim();
  if (linked) return linked;
  if (hint) return hint;
  if (seg?.page) return `Invoice p.${seg.page}`;
  return `Workorder ${(seg?.segmentIndex ?? index) + 1}`;
};

const workorderIdGuessFromSegment = (seg) => {
  const blob = [seg?.linkedWorkorder_id, seg?.hint_workorder_id, seg?.name]
    .filter(Boolean)
    .join(' ');
  const m = blob.match(/WH\d{6,}/i);
  return m ? m[0].toUpperCase().replace(/\s/g, '') : '';
};

const positiveIntFormField = (v) => {
  if (v === '' || v == null) return false;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) && n > 0;
};

const mergeSamplesSentFromRowContext = (formFields, row) => {
  if (!formFields || !row) return;
  if (positiveIntFormField(formFields.samples_sent_shipped)) return;

  const sm = row.sample_count;
  if (sm != null && Number(sm) > 0) {
    formFields.samples_sent_shipped = String(sm);
    return;
  }
  const fromDb = row.samples_sent_shipped_from_results;
  if (fromDb != null && Number.isFinite(Number(fromDb)) && Number(fromDb) >= 0) {
    formFields.samples_sent_shipped = String(fromDb);
  }
};

/** Fill missing prep/analysis/shipped from saved approval, then SM / results pivot. */
const mergeSampleFieldsFromWorkorderContext = (formFields, row) => {
  if (!formFields || !row) return;
  const approval = row.approval;

  if (!positiveIntFormField(formFields.sample_num_prep)) {
    const prep = approval?.sample_num_prep;
    if (prep != null && Number(prep) > 0) {
      formFields.sample_num_prep = String(prep);
    }
  }
  if (!positiveIntFormField(formFields.sample_num_analysis)) {
    const analysis = approval?.sample_num_analysis;
    if (analysis != null && Number(analysis) > 0) {
      formFields.sample_num_analysis = String(analysis);
    }
  }
  if (!positiveIntFormField(formFields.samples_sent_shipped)) {
    const shipped = approval?.samples_sent_shipped;
    if (shipped != null && Number(shipped) > 0) {
      formFields.samples_sent_shipped = String(shipped);
    }
  }

  mergeSamplesSentFromRowContext(formFields, row);
};

/** When AI extract resolves a workorder not yet in the list, hydrate row shape from workorder_meta API. */
const rowStubFromWorkorderMeta = (workorderId, meta) => ({
  workorder_id: workorderId,
  sample_count: meta?.sample_count ?? null,
  samples_sent_shipped_from_results: meta?.samples_sent_shipped_from_results ?? null,
  qaqc_workorder_note: meta?.qaqc_workorder_note ?? null,
  qaqc_passed: meta?.qaqc_passed ?? null,
  hole_ids: Array.isArray(meta?.hole_ids) ? meta.hole_ids : [],
  news_release_dates: Array.isArray(meta?.news_release_dates) ? meta.news_release_dates : [],
  lab_name: null,
  approval: null,
  average_sample_cost_derived: null,
  status: 'pending',
});

const LabApprovals = ({ user }) => {
  const [approvalsData, setApprovalsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRow, setSelectedRow] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [actionInProgress, setActionInProgress] = useState(false);
  /** 'list' = opened from workorder table; 'extract' = opened after AI / DocuSign PDF extract (same modal UI). */
  const [approvalModalSource, setApprovalModalSource] = useState('list');
  const [extractBusy, setExtractBusy] = useState(false);
  const [extractPreviewUrl, setExtractPreviewUrl] = useState(null);
  const [extractModelLabel, setExtractModelLabel] = useState('');
  const fileInputRef = useRef(null);
  /** Last workorder row opened from the list; used to pass SM (sample_count) into AI extract. */
  const extractContextRowRef = useRef(null);
  const docusignIframeRef = useRef(null);
  /** Survives DocuSign panel close so opening the workorder modal still gets the envelope id. */
  const pendingDocusignFolioRef = useRef(null);
  const pendingProcurifyFolioRef = useRef(null);
  /** Active multi-invoice envelope session (return to picker after save / cancel). */
  const multiEnvelopeSessionRef = useRef(null);
  /** Segment hint WH id when AI misses it on a single-page envelope PDF. */
  const pendingSegmentWorkorderHintRef = useRef(null);
  /** Segment last opened for Review (patch picker status after save). */
  const lastReviewedSegmentRef = useRef(null);

  const [docusignAwaitingRows, setDocusignAwaitingRows] = useState([]);
  const [docusignAwaitingLoading, setDocusignAwaitingLoading] = useState(true);
  const [docusignAwaitingError, setDocusignAwaitingError] = useState(null);
  /** DocuSign envelope.status filter in awaiting list (default: sent only). */
  const [docusignEnvelopeStatusFilter, setDocusignEnvelopeStatusFilter] =
    useState('sent');
  const [docusignOpeningId, setDocusignOpeningId] = useState(null);
  /** In-app DocuSign (iframe): Azure redirect URL; follows 302 to DocuSign inside the modal when open. */
  const [docusignPanelUrl, setDocusignPanelUrl] = useState(null);
  const [docusignPanelEnvelopeId, setDocusignPanelEnvelopeId] = useState(null);
  const [procurifySidePanelOpen, setProcurifySidePanelOpen] = useState(false);
  /** Multi-workorder envelope picker (user chooses which invoice to extract). */
  const [envelopeSegmentPicker, setEnvelopeSegmentPicker] = useState(null);
  const [envelopeSegmentPickerBusy, setEnvelopeSegmentPickerBusy] = useState(false);
  /** envelope id (lower) → segmentCount after first segment plan fetch */
  const [envelopeSegmentCounts, setEnvelopeSegmentCounts] = useState({});
  /** envelope id (lower) → cached segment plan for awaiting list + picker */
  const [envelopeSegmentPlans, setEnvelopeSegmentPlans] = useState({});

  const [procurifyOpeningId, setProcurifyOpeningId] = useState(null);
  const [activeProcurifyBill, setActiveProcurifyBill] = useState(null);
  /** 'procurify' | 'docusign' | null — side panel in review modal */
  const [documentSource, setDocumentSource] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await labApprovalsService.getDashboardData({
        search: debouncedSearch,
        status: statusFilter,
      });

      setApprovalsData(data.approvalsData || []);
    } catch (err) {
      console.error('Error loading lab approvals data:', err);
      setError(err.message || 'Failed to load lab approvals data');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const loadDocusignAwaiting = useCallback(async () => {
    setDocusignAwaitingLoading(true);
    setDocusignAwaitingError(null);
    try {
      const res = await docusignService.fetchActionRequiredEnvelopes({
        status: docusignEnvelopeStatusFilter,
      });
      if (!res.body?.success) {
        throw new Error(res.body?.error || `DocuSign list failed (${res.status})`);
      }
      setDocusignAwaitingRows(docusignAwaitingRowsFromApiData(res.body.data));
    } catch (e) {
      setDocusignAwaitingError(e.message || 'Could not load DocuSign queue');
      setDocusignAwaitingRows([]);
    } finally {
      setDocusignAwaitingLoading(false);
    }
  }, [docusignEnvelopeStatusFilter]);

  useEffect(() => {
    loadDocusignAwaiting();
  }, [loadDocusignAwaiting]);

  /** Prefetch workorders per envelope for the awaiting list (cached after first load). */
  useEffect(() => {
    if (docusignAwaitingLoading || !docusignAwaitingRows.length) return undefined;

    let cancelled = false;

    const prefetch = async () => {
      for (const row of docusignAwaitingRows) {
        if (cancelled) break;
        const eid = (row.envelopeId || row.envelope_id || '').trim();
        if (!eid) continue;
        const norm = eid.toLowerCase();

        let skip = false;
        setEnvelopeSegmentPlans((prev) => {
          const existing = prev[norm];
          if (existing?.segments?.length || existing?.loading) {
            skip = true;
            return prev;
          }
          return { ...prev, [norm]: { ...(existing || {}), loading: true } };
        });
        if (skip) continue;

        try {
          const plan = await docusignService.fetchEnvelopeSegments(eid);
          if (cancelled) return;
          setEnvelopeSegmentPlans((prev) => ({
            ...prev,
            [norm]: { ...plan, loading: false, error: false },
          }));
          setEnvelopeSegmentCounts((prev) => ({
            ...prev,
            [norm]: plan.segmentCount,
          }));
        } catch (e) {
          console.warn('DocuSign envelope segment prefetch failed:', eid, e);
          if (!cancelled) {
            setEnvelopeSegmentPlans((prev) => ({
              ...prev,
              [norm]: {
                segments: [],
                segmentCount: 0,
                multi: false,
                loading: false,
                error: true,
              },
            }));
          }
        }
      }
    };

    void prefetch();
    return () => {
      cancelled = true;
    };
  }, [docusignAwaitingRows, docusignAwaitingLoading]);

  const openEditModal = async (row) => {
    if (!row) return;
    setExtractPreviewUrl(null);
    setExtractModelLabel('');
    setApprovalModalSource('list');
    extractContextRowRef.current = row;
    let enriched = { ...row };
    const wid = row.workorder_id;
    if (wid) {
      try {
        const meta = await labApprovalsService.getWorkorderMeta(wid);
        enriched = {
          ...row,
          samples_sent_shipped_from_results:
            meta.samples_sent_shipped_from_results ?? row.samples_sent_shipped_from_results,
          qaqc_workorder_note: meta.qaqc_workorder_note ?? row.qaqc_workorder_note,
        };
        if (meta.qaqc_passed !== undefined && meta.qaqc_passed !== null) {
          enriched.qaqc_passed = meta.qaqc_passed;
        }
      } catch (e) {
        console.warn('Lab Approvals: workorder meta prefetch failed', e);
      }
    }
    extractContextRowRef.current = enriched;
    setSelectedRow(enriched);
    const f = formFromApproval(enriched.approval);
    f.workorder_id = wid != null ? String(wid).trim() : '';
    mergeSamplesSentFromRowContext(f, enriched);
    const acd = enriched.average_sample_cost_derived;
    if (
      (f.average_sample_cost === '' || f.average_sample_cost == null) &&
      acd != null &&
      Number.isFinite(Number(acd))
    ) {
      f.average_sample_cost = Number(acd).toFixed(2);
    }
    // Comments: saved approval first; if empty, QAQC note from qaqc.workorder_notes (see API).
    if (!(f.comments || '').trim() && enriched.qaqc_workorder_note) {
      f.comments = String(enriched.qaqc_workorder_note);
    }
    const pendingFolio = pendingDocusignFolioRef.current?.trim();
    if (pendingFolio && !(f.folio_id || '').trim()) {
      f.folio_id = pendingFolio;
    }
    const pendingProcurifyFolio = pendingProcurifyFolioRef.current?.trim();
    if (pendingProcurifyFolio && !(f.folio_id || '').trim()) {
      f.folio_id = pendingProcurifyFolio;
    }
    appendApprovedByStamp(f, user, enriched);
    setForm(f);
    setShowEditModal(true);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAverageSampleCostBlur = () => {
    setForm((prev) => ({
      ...prev,
      average_sample_cost: roundAverageSampleCostStr(prev.average_sample_cost),
    }));
  };

  const workorderIdForSave = () =>
    (form.workorder_id || selectedRow?.workorder_id || '').trim();

  const payloadFromApprovalForm = (f, workorderId) => {
    const wid = (workorderId || '').trim();
    if (!wid) return null;

    const parseNum = (s) => {
      if (s === '' || s == null) return null;
      const x = Number(s);
      return Number.isFinite(x) ? x : null;
    };

    return {
      workorder_id: wid,
      po_number: f.po_number.trim() || null,
      sample_num_prep: parseNum(f.sample_num_prep),
      sample_num_analysis: parseNum(f.sample_num_analysis),
      samples_sent_shipped: parseNum(f.samples_sent_shipped),
      subtotal: f.subtotal === '' ? null : f.subtotal,
      gst: f.gst === '' ? null : f.gst,
      total: f.total === '' ? null : f.total,
      approved: f.approved === '' ? null : f.approved === 'true',
      approval_date: f.approval_date || null,
      comments: f.comments.trim() || null,
      average_sample_cost: (() => {
        if (f.average_sample_cost === '' || f.average_sample_cost == null) {
          return null;
        }
        const x = Number(String(f.average_sample_cost).replace(/,/g, ''));
        return Number.isFinite(x) ? Number(x.toFixed(2)) : null;
      })(),
      folio_id: (f.folio_id || '').trim() || null,
      software_source: (() => {
        const s = (f.software_source || '').trim().toLowerCase();
        if (s === 'procurify' || s === 'docusign') return s;
        if (formUsesProcurifySource(f, documentSource)) return 'procurify';
        if ((f.folio_id || '').trim()) return 'docusign';
        return null;
      })(),
      procurify_bill_id: (() => {
        const raw = f.procurify_bill_id;
        if (raw === '' || raw == null) return null;
        const n = Number(raw);
        return Number.isFinite(n) ? Math.trunc(n) : null;
      })(),
    };
  };

  const isPdfFile = (f) =>
    !!f &&
    (f.type === 'application/pdf' ||
      (typeof f.name === 'string' && /\.pdf$/i.test(f.name)));

  const isAllowedInvoiceImage = (f) =>
    !!f && f.type && /^image\/(png|jpe?g|gif|webp)$/i.test(f.type);

  const processExtractFile = async (file, options = {}) => {
    const afterExtract = options.afterExtract || 'modal';
    if (!file) return;
    const pdf = isPdfFile(file);
    const imageOk = isAllowedInvoiceImage(file);
    if (!pdf && !imageOk) {
      setError(
        'Please drop or paste an image (PNG, JPEG, GIF, WebP) or a PDF invoice.'
      );
      return;
    }
    const maxBytes = pdf ? 15 * 1024 * 1024 : 8 * 1024 * 1024;
    if (file.size > maxBytes) {
      setError(
        pdf
          ? 'PDF must be 15 MB or smaller.'
          : 'Image must be 8 MB or smaller.'
      );
      return;
    }
    setExtractBusy(true);
    setError(null);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Could not read file'));
        reader.readAsDataURL(file);
      });
      const str = String(dataUrl);
      const comma = str.indexOf(',');
      const meta = comma >= 0 ? str.slice(0, comma) : '';
      const b64 = comma >= 0 ? str.slice(comma + 1) : str;
      const mimeMatch = meta.match(/:(.*?);/);
      let mime = mimeMatch ? mimeMatch[1] : file.type || 'image/png';
      if (pdf && (!mime || mime === 'application/octet-stream')) {
        mime = 'application/pdf';
      }

      const result = await labApprovalsService.extractFromImage({
        imageBase64: b64,
        mimeType: mime,
      });

      setExtractPreviewUrl(str.startsWith('data:') ? str : null);
      setExtractModelLabel(result.meta?.model ? `Model: ${result.meta.model}` : '');

      const extracted = formFromExtracted(result.data);
      const prefill = options.prefillBill;
      if (prefill && typeof prefill === 'object') {
        if (!(extracted.invoice_number || '').trim() && prefill.invoice_number) {
          extracted.invoice_number = String(prefill.invoice_number);
        }
        if (
          (extracted.total === '' || extracted.total == null) &&
          prefill.total_cost_with_tax != null
        ) {
          extracted.total = String(prefill.total_cost_with_tax);
        }
        if (!extracted.invoice_date && prefill.invoice_date) {
          extracted.invoice_date = String(prefill.invoice_date).slice(0, 10);
        }
        if (!extracted.approval_date && prefill.due_date) {
          extracted.approval_date = String(prefill.due_date).slice(0, 10);
        }
        const lineQty = prefill.line_quantity;
        if (
          !(extracted.sample_num_analysis || '').trim() &&
          lineQty != null &&
          Number(lineQty) > 0
        ) {
          extracted.sample_num_analysis = String(Math.trunc(Number(lineQty)));
        }
        if (prefill.uuid) {
          extracted.folio_id = String(prefill.uuid).trim();
        }
        extracted.software_source = 'procurify';
        if (prefill.id != null && prefill.id !== '') {
          extracted.procurify_bill_id = String(prefill.id);
        }
      }
      let wid = (extracted.workorder_id || '').trim();
      const segmentHint = (pendingSegmentWorkorderHintRef.current || '').trim();
      pendingSegmentWorkorderHintRef.current = null;
      if (!wid && segmentHint) {
        wid = segmentHint;
        extracted.workorder_id = wid;
      }

      let ctxRow = null;
      if (wid) {
        const norm = wid.toLowerCase();
        const fromList = approvalsData.find(
          (w) => String(w.workorder_id || '').trim().toLowerCase() === norm
        );
        if (fromList) {
          ctxRow = { ...fromList };
        } else {
          ctxRow = {
            workorder_id: wid,
            approval: null,
            qaqc_passed: null,
            sample_count: null,
            samples_sent_shipped_from_results: null,
            lab_name: null,
            hole_ids: [],
            news_release_dates: [],
            qaqc_workorder_note: null,
          };
        }
        try {
          const meta = await labApprovalsService.getWorkorderMeta(wid);
          ctxRow = {
            ...ctxRow,
            workorder_id: wid,
            sample_count: meta.sample_count ?? ctxRow.sample_count,
            samples_sent_shipped_from_results:
              meta.samples_sent_shipped_from_results ??
              ctxRow.samples_sent_shipped_from_results,
            qaqc_workorder_note: meta.qaqc_workorder_note ?? ctxRow.qaqc_workorder_note,
            qaqc_passed: meta.qaqc_passed ?? ctxRow.qaqc_passed,
            hole_ids:
              Array.isArray(meta.hole_ids) && meta.hole_ids.length
                ? meta.hole_ids
                : ctxRow.hole_ids,
            news_release_dates:
              Array.isArray(meta.news_release_dates) && meta.news_release_dates.length
                ? meta.news_release_dates
                : ctxRow.news_release_dates,
          };
          if (!fromList) {
            ctxRow = { ...rowStubFromWorkorderMeta(wid, meta), ...ctxRow };
          }
        } catch (e) {
          console.warn('Lab Approvals: workorder meta for extract failed', e);
        }
        mergeSampleFieldsFromWorkorderContext(extracted, ctxRow);
      }

      const fromDocusignPdf =
        typeof file?.name === 'string' && /^docusign-/i.test(file.name);
      const fromProcurifyPdf =
        typeof file?.name === 'string' && /^procurify-/i.test(file.name);
      if (fromProcurifyPdf) {
        setDocumentSource('procurify');
      } else if (fromDocusignPdf) {
        setDocumentSource('docusign');
      }
      const pendingFolio = fromDocusignPdf
        ? pendingDocusignFolioRef.current?.trim()
        : fromProcurifyPdf
          ? pendingProcurifyFolioRef.current?.trim()
          : '';
      if (pendingFolio && !(extracted.folio_id || '').trim()) {
        extracted.folio_id = pendingFolio;
      }

      if (
        !(extracted.comments || '').trim() &&
        ctxRow?.qaqc_workorder_note
      ) {
        extracted.comments = String(ctxRow.qaqc_workorder_note);
      }
      appendApprovedByStamp(extracted, user, ctxRow);

      extractContextRowRef.current = ctxRow;
      setSelectedRow(
        ctxRow || {
          workorder_id: wid || '',
          approval: null,
          qaqc_passed: null,
          sample_count: null,
          samples_sent_shipped_from_results: null,
          lab_name: null,
          hole_ids: [],
          news_release_dates: [],
          qaqc_workorder_note: null,
        }
      );
      setForm(extracted);
      setApprovalModalSource('extract');

      if (afterExtract === 'saveAndApprove') {
        const saveWid = (extracted.workorder_id || wid || '').trim();
        const payload = payloadFromApprovalForm(extracted, saveWid);
        if (!payload) {
          setShowEditModal(true);
          return { openedModal: true };
        }
        if (ctxRow?.qaqc_passed !== true) {
          setShowEditModal(true);
          return { openedModal: true };
        }
        setActionInProgress(true);
        try {
          await labApprovalsService.saveAndApproveWorkorder(payload);
          patchSegmentPickerWorkorder(payload.workorder_id);
          await upsertSavedWorkorderInApprovalsList(payload, true);
          await loadDashboardData();
          return { saved: true };
        } finally {
          setActionInProgress(false);
        }
      }

      setShowEditModal(true);
      return { openedModal: true };
    } catch (err) {
      console.error('Document extract failed:', err);
      setError(err.message || 'Document extraction failed');
      return { error: err };
    } finally {
      setExtractBusy(false);
    }
  };

  const patchSegmentPickerWorkorder = (workorderId) => {
    const seg = lastReviewedSegmentRef.current;
    const wid = (workorderId || '').trim();
    if (!seg || !wid) return;

    const patchSegments = (segments) =>
      (segments || []).map((s) =>
        s.segmentIndex === seg.segmentIndex
          ? {
              ...s,
              linkedWorkorder_id: wid,
              hint_workorder_id: (s.hint_workorder_id || '').trim() || wid,
            }
          : s
      );

    setEnvelopeSegmentPicker((prev) =>
      prev ? { ...prev, segments: patchSegments(prev.segments) } : prev
    );
    if (multiEnvelopeSessionRef.current?.segments) {
      multiEnvelopeSessionRef.current = {
        ...multiEnvelopeSessionRef.current,
        segments: patchSegments(multiEnvelopeSessionRef.current.segments),
      };
    }
    const eidNorm = (
      multiEnvelopeSessionRef.current?.envelopeId ||
      docusignPanelEnvelopeId ||
      pendingDocusignFolioRef.current ||
      ''
    )
      .trim()
      .toLowerCase();
    if (eidNorm) {
      setEnvelopeSegmentPlans((prev) => {
        const plan = prev[eidNorm];
        if (!plan?.segments?.length) return prev;
        return {
          ...prev,
          [eidNorm]: { ...plan, segments: patchSegments(plan.segments) },
        };
      });
    }
  };

  const upsertSavedWorkorderInApprovalsList = async (payload, approvedFlag) => {
    const ctx = extractContextRowRef.current || selectedRow;
    setApprovalsData((prev) =>
      mergeSavedWorkorderIntoApprovalsData(prev, payload, ctx, approvedFlag)
    );
    try {
      const data = await labApprovalsService.getDashboardData({
        search: payload.workorder_id,
        status: 'all',
      });
      const norm = String(payload.workorder_id || '').trim().toLowerCase();
      const freshRow = (data.approvalsData || []).find(
        (w) => String(w.workorder_id || '').trim().toLowerCase() === norm
      );
      if (freshRow) {
        setApprovalsData((prev) => {
          const i = prev.findIndex(
            (w) => String(w.workorder_id || '').trim().toLowerCase() === norm
          );
          if (i >= 0) {
            const next = [...prev];
            next[i] = freshRow;
            return next;
          }
          return [freshRow, ...prev];
        });
      }
    } catch (e) {
      console.warn('Lab Approvals: post-save workorder refresh failed', e);
    }
  };

  const runEnvelopeSegmentExtract = async (envelopeId, segment) => {
    const id = (envelopeId || '').trim();
    if (!id || !segment) return;
    lastReviewedSegmentRef.current = segment;
    pendingDocusignFolioRef.current = id;
    const segIdx = segment.segmentIndex ?? 0;
    const blob = await docusignService.fetchEnvelopePdfBlob(id, {
      documentId: segment.documentId,
      page: segment.page,
    });
    const file = new File([blob], `docusign-${id}-seg-${segIdx}.pdf`, {
      type: 'application/pdf',
    });
    pendingSegmentWorkorderHintRef.current = (segment.hint_workorder_id || '').trim() || null;
    await processExtractFile(file);
  };

  const applyEnvelopeSegmentPlan = (envelopeId, plan) => {
    const id = (envelopeId || '').trim();
    if (!id || !plan) return;
    const norm = id.toLowerCase();
    setEnvelopeSegmentPlans((prev) => ({
      ...prev,
      [norm]: { ...plan, loading: false, error: false },
    }));
    setEnvelopeSegmentCounts((prev) => ({
      ...prev,
      [norm]: plan.segmentCount,
    }));
    if (plan.multi && (plan.segments?.length ?? 0) > 1) {
      const payload = {
        envelopeId: id,
        segments: plan.segments,
        segmentCount: plan.segmentCount,
        multi: plan.multi,
      };
      multiEnvelopeSessionRef.current = payload;
      setEnvelopeSegmentPicker(payload);
    } else {
      multiEnvelopeSessionRef.current = null;
      setEnvelopeSegmentPicker(null);
    }
  };

  const refreshEnvelopeSegmentPicker = async (envelopeId) => {
    const id = (envelopeId || '').trim();
    if (!id) return null;
    setEnvelopeSegmentPickerBusy(true);
    setError(null);
    try {
      const plan = await docusignService.fetchEnvelopeSegments(id);
      applyEnvelopeSegmentPlan(id, plan);
      return plan;
    } catch (e) {
      console.error('Envelope segment plan failed:', e);
      setError(e.message || 'Could not load workorders in envelope');
      return null;
    } finally {
      setEnvelopeSegmentPickerBusy(false);
    }
  };

  const closeEnvelopeSegmentPicker = () => {
    multiEnvelopeSessionRef.current = null;
    setEnvelopeSegmentPicker(null);
  };

  useEffect(() => {
    if (!envelopeSegmentPicker?.multi || showEditModal) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeEnvelopeSegmentPicker();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [envelopeSegmentPicker?.multi, showEditModal]);

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedRow(null);
    setForm(emptyForm());
    setApprovalModalSource('list');
    setExtractPreviewUrl(null);
    setExtractModelLabel('');

    const session = multiEnvelopeSessionRef.current;
    if (session?.multi && session.envelopeId) {
      setEnvelopeSegmentPicker({
        envelopeId: session.envelopeId,
        segments: session.segments,
        segmentCount: session.segmentCount,
        multi: session.multi,
      });
      return;
    }

    setDocusignPanelUrl(null);
    setDocusignPanelEnvelopeId(null);
    setProcurifySidePanelOpen(false);
    setDocumentSource(null);
    setActiveProcurifyBill(null);
  };

  const openProcurifyBillInPanel = (folioUuid, billMeta = null) => {
    const uid = String(folioUuid || '').trim();
    if (!uid) return;
    setDocusignPanelUrl(null);
    setDocusignPanelEnvelopeId(null);
    setDocumentSource('procurify');
    setProcurifySidePanelOpen(true);
    setActiveProcurifyBill(
      billMeta && typeof billMeta === 'object'
        ? { ...billMeta, uuid: uid }
        : { uuid: uid }
    );
  };

  const closeProcurifyPanel = () => {
    setProcurifySidePanelOpen(false);
  };

  const handleOpenProcurifyBill = async (bill) => {
    if (!bill) return;
    const openId = bill.uuid || bill.id;
    setProcurifyOpeningId(openId);
    setDocumentSource('procurify');
    setDocusignPanelUrl(null);
    setDocusignPanelEnvelopeId(null);
    setError(null);
    const folio = (bill.uuid || (bill.id != null ? String(bill.id) : '')).trim();
    if (folio) {
      pendingProcurifyFolioRef.current = folio;
    }
    let enrichedBill = bill;
    try {
      if (bill.line_quantity == null) {
        try {
          enrichedBill = await procurifyLabService.fetchBillDetail(bill.uuid || bill.id);
        } catch (detailErr) {
          console.warn('Procurify bill detail fetch failed; using list row', detailErr);
          enrichedBill = bill;
        }
      }
      setActiveProcurifyBill(enrichedBill);
      setProcurifySidePanelOpen(true);
      const blob = await procurifyLabService.fetchBillPdfBlob(bill.uuid || bill.id);
      const file = new File([blob], `procurify-${folio || openId}.pdf`, {
        type: 'application/pdf',
      });
      await processExtractFile(file, { prefillBill: enrichedBill });
    } catch (e) {
      console.error('Procurify bill open failed:', e);
      setError(e.message || 'Could not load Procurify invoice PDF');
    } finally {
      setProcurifyOpeningId(null);
    }
  };

  const handleEnvelopeSegmentReview = async (segment) => {
    const eid = envelopeSegmentPicker?.envelopeId;
    if (!eid || !segment) return;
    setEnvelopeSegmentPickerBusy(true);
    setError(null);
    try {
      await runEnvelopeSegmentExtract(eid, segment);
    } catch (e) {
      console.error('Segment extract failed:', e);
      setError(e.message || 'Invoice extract failed');
    } finally {
      setEnvelopeSegmentPickerBusy(false);
    }
  };

  const handleEnvelopeSegmentSaveAndApprove = async (segment) => {
    const eid = envelopeSegmentPicker?.envelopeId;
    if (!eid || !segment) return;
    lastReviewedSegmentRef.current = segment;
    pendingDocusignFolioRef.current = eid;
    pendingSegmentWorkorderHintRef.current =
      (segment.hint_workorder_id || workorderIdGuessFromSegment(segment) || '').trim() ||
      null;
    setEnvelopeSegmentPickerBusy(true);
    setError(null);
    try {
      const segIdx = segment.segmentIndex ?? 0;
      const blob = await docusignService.fetchEnvelopePdfBlob(eid, {
        documentId: segment.documentId,
        page: segment.page,
      });
      const file = new File([blob], `docusign-${eid}-seg-${segIdx}.pdf`, {
        type: 'application/pdf',
      });
      await processExtractFile(file, { afterExtract: 'saveAndApprove' });
    } catch (e) {
      console.error('Segment save and approve failed:', e);
      setError(e.message || 'Save and approve failed');
    } finally {
      setEnvelopeSegmentPickerBusy(false);
    }
  };

  const openOtherWorkordersInEnvelope = () => {
    const id = (
      docusignPanelEnvelopeId ||
      form.folio_id ||
      pendingDocusignFolioRef.current ||
      ''
    ).trim();
    if (id) void refreshEnvelopeSegmentPicker(id);
  };

  const closeDocusignPanel = () => {
    setDocusignPanelUrl(null);
    setDocusignPanelEnvelopeId(null);
  };

  /** After signing, DocuSign redirects the iframe to our app — close the signing pane instead of nesting the SPA. */
  const handleDocusignIframeLoad = useCallback(() => {
    try {
      const win = docusignIframeRef.current?.contentWindow;
      if (!win) return;
      if (win.location.origin !== window.location.origin) return;
      const h = win.location.hash || '';
      if (h.includes('lab-approvals')) {
        setDocusignPanelUrl(null);
        setDocusignPanelEnvelopeId(null);
        void loadDocusignAwaiting();
        void loadDashboardData();
      }
    } catch {
      /* cross-origin until return URL loads */
    }
  }, [loadDocusignAwaiting, loadDashboardData]);

  /**
   * DocuSign opens in an iframe inside the approval modal (when the modal is open). No window.open (no pop-up block).
   * Use "Open in new tab" in the signing toolbar if DocuSign blocks iframes.
   */
  const openDocusignEnvelopeInNewTab = async (envelopeId, options = {}) => {
    const { withInvoiceExtract = true, persistFolio } = options;
    const id = (envelopeId || '').trim();
    if (!id) return;
    pendingDocusignFolioRef.current = id;
    setDocusignOpeningId(id);
    setDocumentSource('docusign');
    setActiveProcurifyBill(null);
    setError(null);
    const returnUrl = `${window.location.origin}${window.location.pathname}${window.location.search || ''}#/lab-approvals`;
    let redirectUrl;
    try {
      redirectUrl = docusignService.getEnvelopeWebRedirectUrl(id, returnUrl);
    } catch (e) {
      setDocusignOpeningId(null);
      setError(e.message || 'Invalid envelope id');
      return;
    }
    setProcurifySidePanelOpen(false);
    setActiveProcurifyBill(null);
    setDocusignPanelUrl(redirectUrl);
    setDocusignPanelEnvelopeId(id);
    if (showEditModal) {
      setForm((prev) => ({
        ...prev,
        folio_id: id,
        software_source: 'docusign',
        procurify_bill_id: '',
      }));
    }
    const doPersist =
      persistFolio === false
        ? false
        : Boolean(showEditModal && selectedRow?.workorder_id);
    try {
      const sideEffects = [];
      if (doPersist && selectedRow?.workorder_id) {
        sideEffects.push(
          labApprovalsService
            .saveWorkorderApproval({
              workorder_id: selectedRow.workorder_id,
              folio_id: id,
            })
            .then(() => {
              setForm((prev) => ({ ...prev, folio_id: id }));
              return loadDashboardData();
            })
        );
      }
      if (withInvoiceExtract) {
        sideEffects.push(
          (async () => {
            const plan = await docusignService.fetchEnvelopeSegments(id);
            applyEnvelopeSegmentPlan(id, plan);
            if (plan.multi && (plan.segments?.length ?? 0) > 1) {
              return;
            }
            const seg = plan.segments?.[0];
            const blob = await docusignService.fetchEnvelopePdfBlob(id, {
              documentId: seg?.documentId,
              page: seg?.page,
            });
            const file = new File([blob], `docusign-${id}.pdf`, {
              type: 'application/pdf',
            });
            await processExtractFile(file);
          })()
        );
      }
      if (sideEffects.length) {
        await Promise.all(sideEffects);
      }
    } catch (e) {
      console.error('DocuSign follow-up failed:', e);
      setError(
        e.message ||
          'DocuSign signing is open, but saving the folio or invoice extract failed.'
      );
    } finally {
      setDocusignOpeningId(null);
    }
  };

  const handleDropZoneDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer?.files?.[0];
    if (f) processExtractFile(f);
  };

  const handleDropZonePaste = (e) => {
    const cd = e.clipboardData;
    if (!cd) return;
    const fromFiles = cd.files && cd.files.length > 0 ? cd.files[0] : null;
    if (
      fromFiles &&
      (isPdfFile(fromFiles) || isAllowedInvoiceImage(fromFiles))
    ) {
      e.preventDefault();
      processExtractFile(fromFiles);
      return;
    }
    const items = cd.items;
    if (!items) return;
    for (let i = 0; i < items.length; i += 1) {
      const it = items[i];
      if (it.kind === 'file' && it.type === 'application/pdf') {
        e.preventDefault();
        const f = it.getAsFile();
        if (f) processExtractFile(f);
        return;
      }
      if (it.type && it.type.startsWith('image/')) {
        e.preventDefault();
        const f = it.getAsFile();
        if (f) processExtractFile(f);
        break;
      }
    }
  };

  const handleSaveApproval = async () => {
    const payload = payloadFromApprovalForm(form, workorderIdForSave());
    if (!payload) {
      setError('Workorder ID is required before saving.');
      return;
    }

    try {
      setActionInProgress(true);
      await labApprovalsService.saveWorkorderApproval(payload);
      patchSegmentPickerWorkorder(payload.workorder_id);
      await upsertSavedWorkorderInApprovalsList(payload, null);
      await loadDashboardData();
      closeEditModal();
    } catch (err) {
      console.error('Error saving approval:', err);
      setError(err.message || 'Failed to save');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleSaveAndApprove = async () => {
    const payload = payloadFromApprovalForm(form, workorderIdForSave());
    if (!payload) {
      setError('Workorder ID is required before saving.');
      return;
    }
    if (selectedRow?.qaqc_passed !== true) return;

    try {
      setActionInProgress(true);
      await labApprovalsService.saveAndApproveWorkorder(payload);
      patchSegmentPickerWorkorder(payload.workorder_id);
      await upsertSavedWorkorderInApprovalsList(payload, true);
      await loadDashboardData();
      closeEditModal();
    } catch (err) {
      console.error('Error saving and approving:', err);
      setError(err.message || 'Failed to save and approve');
    } finally {
      setActionInProgress(false);
    }
  };

  const summaryStats = React.useMemo(() => {
    const pending = approvalsData.filter(
      (item) => (item.status || 'pending').toLowerCase() === 'pending'
    ).length;
    const approved = approvalsData.filter(
      (item) => (item.status || '').toLowerCase() === 'approved'
    ).length;
    const rejected = approvalsData.filter(
      (item) => (item.status || '').toLowerCase() === 'rejected'
    ).length;

    return { pending, approved, rejected, total: approvalsData.length };
  }, [approvalsData]);

  /** Workorder row keyed by DocuSign envelope id saved as approval.folio_id. */
  const approvalByFolioId = React.useMemo(() => {
    const m = new Map();
    for (const item of approvalsData) {
      const raw = item.approval?.folio_id;
      const fid = raw != null ? String(raw).trim().toLowerCase() : '';
      if (fid) m.set(fid, item);
    }
    return m;
  }, [approvalsData]);

  const approvalsByFolioWorkorder = React.useMemo(
    () => buildApprovalsByFolioWorkorder(approvalsData),
    [approvalsData]
  );

  const computedAvgCost =
    form.total !== '' &&
    form.sample_num_analysis !== '' &&
    Number(form.sample_num_analysis) > 0
      ? Number(form.total) / Number(form.sample_num_analysis)
      : null;

  return (
    <Layout user={user} currentRoute="/lab-approvals">
      <div className="lab-approvals-shell">
        <div className="lab-approvals lab-approvals-shell__main">
        <Tabs
          className="lab-approvals-source-tabs"
          defaultTab="procurify"
          tabs={[
            {
              id: 'procurify',
              label: 'Procurify',
              content: (
                <LabApprovalsProcurifyPanel
                  user={user}
                  approvalByFolioId={approvalByFolioId}
                  linkedWorkorderBadge={linkedWorkorderBadge}
                  onOpenBill={handleOpenProcurifyBill}
                  openingBillId={procurifyOpeningId}
                />
              ),
            },
            {
              id: 'docusign',
              label: 'DocuSign',
              badge:
                !docusignAwaitingLoading && docusignAwaitingRows.length > 0
                  ? String(docusignAwaitingRows.length)
                  : undefined,
              content: (
        <section className="lab-approvals-docusign" aria-label="DocuSign envelopes awaiting your signature">
          <div className="lab-approvals-docusign__head">
            <h2 className="lab-approvals-docusign__title">Awaiting your signature (DocuSign)</h2>
            <p className="lab-approvals-docusign__hint">
              Loaded from DocuSign with the <strong>Status</strong> filter (default{' '}
              <code className="lab-approvals-code">sent</code> — fast API path;{' '}
              <code className="lab-approvals-code">All</code> merges inbox and Spam). Same account as{' '}
              <code className="lab-approvals-code">DOCUSIGN_ACCOUNT_ID</code>.
              — same queue as “Needs to Sign”. Click opens DocuSign and runs invoice <strong>AI extract</strong> (or a
              workorder picker when the envelope has multiple invoices). The review modal shows DocuSign{' '}
              <strong>alongside the form</strong>. The envelope id fills{' '}
              <code className="lab-approvals-code">folio_id</code> when the modal is open. After
              signing, the signing pane closes when DocuSign returns here; use <strong>Open in new tab</strong> if
              the iframe stays blank. When an envelope matches a <code className="lab-approvals-code">folio_id</code>{' '}
              on a workorder below, its lab status appears on the row (same labels as the workorder list).
            </p>
            <div className="lab-approvals-docusign__toolbar">
              <label className="lab-approvals-docusign__filter">
                <span className="lab-approvals-docusign__filter-label">Status</span>
                <select
                  className="status-filter lab-approvals-docusign__filter-select"
                  value={docusignEnvelopeStatusFilter}
                  onChange={(e) => setDocusignEnvelopeStatusFilter(e.target.value)}
                  disabled={docusignAwaitingLoading}
                  aria-label="Filter DocuSign envelopes by status"
                >
                  <option value="sent">Sent &amp; delivered (needs signature)</option>
                  <option value="delivered">Delivered only</option>
                  <option value="completed">Completed</option>
                  <option value="all">All</option>
                </select>
              </label>
              {!docusignAwaitingLoading && docusignAwaitingRows.length > 0 ? (
                <span className="lab-approvals-docusign__filter-count">
                  {docusignAwaitingRows.length} envelope
                  {docusignAwaitingRows.length === 1 ? '' : 's'}
                </span>
              ) : null}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={loadDocusignAwaiting}
                disabled={docusignAwaitingLoading}
              >
                Refresh
              </Button>
            </div>
          </div>
          {docusignAwaitingLoading && (
            <p className="lab-approvals-docusign__status">Loading DocuSign…</p>
          )}
          {docusignAwaitingError && (
            <p className="lab-approvals-docusign__error" role="alert">
              {docusignAwaitingError}
            </p>
          )}
          {!docusignAwaitingLoading &&
            !docusignAwaitingError &&
            docusignAwaitingRows.length === 0 && (
            <p className="lab-approvals-docusign__empty">No envelopes waiting for your signature.</p>
          )}
          {docusignAwaitingRows.length > 0 && (
            <ul className="lab-approvals-docusign__list">
              {docusignAwaitingRows.map((row) => {
                const eid = row.envelopeId || row.envelope_id;
                const subject = row.emailSubject || row.subject || '—';
                const status = row.status || '—';
                const eidNorm = (eid || '').trim().toLowerCase();
                const linkedWo = eidNorm ? approvalByFolioId.get(eidNorm) : null;
                const linkBadge = linkedWorkorderBadge(linkedWo);
                const segCount = eidNorm ? envelopeSegmentCounts[eidNorm] : null;
                const planEntry = eidNorm ? envelopeSegmentPlans[eidNorm] : null;
                const planSegments = planEntry?.segments;
                return (
                  <li key={eid || subject + status}>
                    <button
                      type="button"
                      className="lab-approvals-docusign__row"
                      disabled={!eid || docusignOpeningId === eid}
                      onClick={() => openDocusignEnvelopeInNewTab(eid)}
                      title="Open DocuSign + extract (picker when multiple workorders in envelope)"
                    >
                      <span className="lab-approvals-docusign__subj">{subject}</span>
                      {planEntry?.loading ? (
                        <span className="lab-approvals-docusign__wo-list-loading">
                          Loading workorders in envelope…
                        </span>
                      ) : null}
                      {!planEntry?.loading && planSegments?.length > 0 ? (
                        <span
                          className="lab-approvals-docusign__wo-list"
                          aria-label="Workorders in this envelope"
                        >
                          {planSegments.map((seg, segIdx) => {
                            const label = segmentDisplayLabel(seg, segIdx);
                            const segRow = resolveSegmentDashboardRow(
                              seg,
                              eid,
                              approvalsByFolioWorkorder,
                              approvalsData
                            );
                            const segBadge = linkedWorkorderBadge(segRow);
                            return (
                              <span
                                key={`${seg.segmentIndex}-${seg.documentId}-${seg.page}-${label}`}
                                className={`lab-approvals-docusign__wo-chip${
                                  segBadge
                                    ? ` lab-approvals-docusign__wo-chip--${segBadge.key}`
                                    : ''
                                }`}
                                title={segBadge?.title || label}
                              >
                                {label}
                              </span>
                            );
                          })}
                        </span>
                      ) : null}
                      <span className="lab-approvals-docusign__meta">
                        <span className="lab-approvals-docusign__meta-primary">
                          {status}
                          {segCount > 1 ? (
                            <span className="lab-approvals-docusign__seg-count">
                              {segCount} workorders
                            </span>
                          ) : null}
                          {eid ? (
                            <span className="lab-approvals-docusign__eid">{eid}</span>
                          ) : null}
                        </span>
                        {linkBadge ? (
                          <span
                            className={`lab-approvals-docusign__link-badge lab-approvals-docusign__link-badge--${linkBadge.key}`}
                            title={linkBadge.title}
                          >
                            {linkBadge.wid ? `${linkBadge.wid} · ` : ''}
                            {linkBadge.label}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
              ),
            },
          ]}
        />

        <div className="lab-approvals-header lab-approvals-header--split">
          <div className="lab-approvals-header__intro">
            <h1>Lab Approvals</h1>
            <p className="lab-approvals-sub">
              ALS workorders · QAQC from drilling progress · costing in{' '}
              <code className="lab-approvals-code">als_approvals.workorder_approvals</code>
            </p>
          </div>
          <div className="lab-approvals-header__invoice">
            <h2 className="lab-approvals-invoice-title">Invoice document</h2>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,application/pdf,.pdf"
              className="overview-file-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) processExtractFile(f);
                e.target.value = '';
              }}
            />
            <div
              className={`invoice-dropzone invoice-dropzone--header${
                extractBusy ? ' invoice-dropzone--busy' : ''
              }`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={handleDropZoneDrop}
              onPaste={handleDropZonePaste}
            >
              {extractBusy ? (
                <span>Analyzing document…</span>
              ) : (
                <>
                  <span className="invoice-dropzone__strong">
                    Drop image or PDF here
                  </span>
                  <span className="invoice-dropzone__muted">
                    PNG, JPEG, GIF, WebP, PDF · click to browse · paste (Ctrl+V)
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {loading && (
          <div className="loading-container">
            <Spinner size="large" />
            <p>Loading lab approvals data...</p>
          </div>
        )}

        {error && (
          <Card className="error-card">
            <h3>Error</h3>
            <p>{error}</p>
            <Button onClick={loadDashboardData}>Retry</Button>
          </Card>
        )}

        {!loading && !error && (
          <>
            <Card className="summary-card" padding="sm">
              <h2 className="summary-card__title">Overview</h2>
              <div className="summary-grid">
                <div className="stat-item">
                  <span className="stat-label">Total (this page)</span>
                  <span className="stat-value">{summaryStats.total}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Pending</span>
                  <span className="stat-value stat-pending">{summaryStats.pending}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Approved</span>
                  <span className="stat-value stat-approved">{summaryStats.approved}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Rejected</span>
                  <span className="stat-value stat-rejected">{summaryStats.rejected}</span>
                </div>
              </div>
            </Card>

            <Card className="controls-card" padding="sm">
              <div className="controls-toolbar" role="search">
                <div className="controls-toolbar__search">
                  <label className="visually-hidden" htmlFor="lab-approvals-search">
                    Search workorders
                  </label>
                  <input
                    id="lab-approvals-search"
                    type="search"
                    placeholder="Search workorder, number, lab…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div className="controls-toolbar__filter">
                  <label htmlFor="lab-approvals-filter">Filter</label>
                  <select
                    id="lab-approvals-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="status-filter"
                  >
                    <option value="all">All</option>
                    <option value="pending">Pending approval</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="qaqc_passed">QAQC passed</option>
                    <option value="qaqc_failed">QAQC not passed</option>
                  </select>
                </div>
                <Button
                  onClick={loadDashboardData}
                  variant="secondary"
                  size="small"
                  className="controls-toolbar__refresh"
                >
                  Refresh
                </Button>
              </div>
            </Card>

            <Card className="content-card" padding="sm">
              <h2 className="content-card__title">
                Workorders
                <span className="content-card__count">{approvalsData.length}</span>
              </h2>

              {approvalsData.length === 0 ? (
                <div className="no-data">
                  <div className="no-data-icon"><SelkirkIcon name={ICON.uiClipboardList} size={48} decorative /></div>
                  <h3>No rows</h3>
                  <p>Adjust filters or search, or confirm the API returns data.</p>
                </div>
              ) : (
                <div className="approvals-list">
                  {approvalsData.map((item) => {
                    const qaqcTitle =
                      item.qaqc_passed === true
                        ? 'QAQC passed'
                        : item.qaqc_passed === false
                          ? 'QAQC not passed'
                          : 'QAQC not available';
                    const qaqcShort =
                      item.qaqc_passed === true
                        ? 'Pass'
                        : item.qaqc_passed === false
                          ? 'Fail'
                          : '—';
                    const widNorm = String(item.workorder_id || '')
                      .trim()
                      .toLowerCase();
                    const wnumNorm = String(item.workorder_number || '')
                      .trim()
                      .toLowerCase();
                    const showAltNumber =
                      item.workorder_number &&
                      wnumNorm.length > 0 &&
                      wnumNorm !== widNorm;
                    const holesList = Array.isArray(item.hole_ids) ? item.hole_ids : [];
                    const nrDates = Array.isArray(item.news_release_dates)
                      ? item.news_release_dates
                      : [];
                    const holesTitle = holesList.length ? holesList.join(', ') : '—';
                    const nrTitle = nrDates.length ? nrDates.join(', ') : '—';
                    const flowState = getLabApprovalFlowState(
                      item.qaqc_passed,
                      item.approval?.approved === true
                    );
                    return (
                      <div
                        key={item.workorder_id}
                        className="approval-item approval-item--clickable"
                        role="button"
                        tabIndex={0}
                        aria-label={`Edit approval for workorder ${item.workorder_id || 'unknown'}`}
                        onClick={() => openEditModal(item)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openEditModal(item);
                          }
                        }}
                      >
                        <div className="approval-item__id">
                          <LabApprovalFlowIcon state={flowState} />
                          <span className="approval-id-main">
                            {item.workorder_id || 'N/A'}
                          </span>
                          {showAltNumber ? (
                            <span className="wo-num" title="ALS workorder number">
                              {item.workorder_number}
                            </span>
                          ) : null}
                        </div>
                        <div
                          className="approval-item__meta"
                          title={[
                            `ALS: ${item.als_status || '—'}`,
                            `Submitted: ${item.sample_submittal_date || '—'}`,
                            `Samples: ${item.sample_count ?? '—'}`,
                            `Lab: ${item.lab_name || '—'}`,
                            `P.O: ${item.als_po_number || '—'}`,
                            `Total: ${item.approval?.total != null ? item.approval.total : '—'}`,
                            `Results rows: ${item.samples_sent_shipped_from_results ?? '—'}`,
                            `Avg/sample: ${
                              item.approval?.average_sample_cost != null
                                ? item.approval.average_sample_cost
                                : item.average_sample_cost_derived ?? '—'
                            }`,
                            `Holes (news release): ${holesTitle}`,
                            `News release date(s): ${nrTitle}`,
                            item.qaqc_workorder_note
                              ? `QAQC note: ${item.qaqc_workorder_note}`
                              : 'QAQC note: —',
                            item.approval?.folio_id
                              ? `Document folio (${item.approval.software_source || 'legacy'}): ${item.approval.folio_id}`
                              : 'Document folio: —',
                          ].join(' · ')}
                        >
                          <span className="meta-kv">
                            <span className="meta-k">ALS</span>
                            <span className="meta-v">{item.als_status || '—'}</span>
                          </span>
                          <span className="meta-dot" aria-hidden>
                            ·
                          </span>
                          <span className="meta-kv">
                            <span className="meta-k">Date</span>
                            <span className="meta-v">
                              {item.sample_submittal_date || '—'}
                            </span>
                          </span>
                          <span className="meta-dot" aria-hidden>
                            ·
                          </span>
                          <span className="meta-kv">
                            <span className="meta-k">Sm</span>
                            <span className="meta-v">{item.sample_count ?? '—'}</span>
                          </span>
                          <span className="meta-dot" aria-hidden>
                            ·
                          </span>
                          <span className="meta-kv meta-kv--grow">
                            <span className="meta-k">Lab</span>
                            <span className="meta-v meta-v--truncate">
                              {item.lab_name || '—'}
                            </span>
                          </span>
                          <span className="meta-dot" aria-hidden>
                            ·
                          </span>
                          <span className="meta-kv">
                            <span className="meta-k">P.O</span>
                            <span className="meta-v">{item.als_po_number || '—'}</span>
                          </span>
                          <span className="meta-dot" aria-hidden>
                            ·
                          </span>
                          <span className="meta-kv">
                            <span className="meta-k">Total</span>
                            <span className="meta-v">
                              {item.approval?.total != null
                                ? item.approval.total
                                : '—'}
                            </span>
                          </span>
                          <span className="meta-dot" aria-hidden>
                            ·
                          </span>
                          <span className="meta-kv" title="COUNT(*) from als_raw.mvw_results_pivot">
                            <span className="meta-k">Shp</span>
                            <span className="meta-v">
                              {item.samples_sent_shipped_from_results != null
                                ? item.samples_sent_shipped_from_results
                                : '—'}
                            </span>
                          </span>
                          <span className="meta-dot" aria-hidden>
                            ·
                          </span>
                          <span
                            className="meta-kv"
                            title="Average sample cost (saved, or total ÷ sample num analysis)"
                          >
                            <span className="meta-k">Avg</span>
                            <span className="meta-v">
                              {item.approval?.average_sample_cost != null
                                ? item.approval.average_sample_cost
                                : item.average_sample_cost_derived != null
                                  ? Number(item.average_sample_cost_derived).toFixed(2)
                                  : '—'}
                            </span>
                          </span>
                          <span className="meta-dot" aria-hidden>
                            ·
                          </span>
                          <span
                            className="meta-kv meta-kv--grow"
                            title={holesTitle === '—' ? 'No holes in news release' : holesTitle}
                          >
                            <span className="meta-k">Holes</span>
                            <span className="meta-v meta-v--truncate">
                              {formatHoleIdsShort(holesList) || '—'}
                            </span>
                          </span>
                          <span className="meta-dot" aria-hidden>
                            ·
                          </span>
                          <span
                            className="meta-kv"
                            title={nrTitle === '—' ? 'No news release date' : nrTitle}
                          >
                            <span className="meta-k">NR</span>
                            <span className="meta-v meta-v--truncate">
                              {formatNewsDatesShort(nrDates) || '—'}
                            </span>
                          </span>
                        </div>
                        <div className="approval-item__badges">
                          <span
                            className={`qaqc-pill qaqc-${
                              item.qaqc_passed === true
                                ? 'yes'
                                : item.qaqc_passed === false
                                  ? 'no'
                                  : 'unk'
                            }`}
                            title={qaqcTitle}
                          >
                            {qaqcShort}
                          </span>
                          <span
                            className={`approval-status status-${(
                              item.status || 'pending'
                            ).toLowerCase()}`}
                          >
                            {item.status || 'Pending'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </>
        )}

        {showEditModal && selectedRow && (() => {
          const editFlowState = getLabApprovalFlowState(
            selectedRow.qaqc_passed,
            form.approved === 'true'
          );
          const showProcurifySidePanel =
            procurifySidePanelOpen &&
            documentSource === 'procurify' &&
            Boolean(
              activeProcurifyBill?.uuid ||
                activeProcurifyBill?.id ||
                (form.folio_id || '').trim()
            );
          const modalSplitOpen = Boolean(docusignPanelUrl || showProcurifySidePanel);
          return (
          <div className="modal-overlay" onClick={closeEditModal}>
            <div
              className={`modal-content modal-content--wide${
                modalSplitOpen ? ' modal-content--lab-with-docusign' : ''
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <Card
                className={`modal-card modal-card--lab-flow modal-card--lab-flow--${editFlowState}${
                  modalSplitOpen ? ' modal-card--lab-with-docusign' : ''
                }`}
              >
                <div className="modal-header modal-header--lab-flow">
                  <div className="modal-header__title-block">
                    <div className="modal-header__title-row">
                      <LabApprovalFlowIcon
                        state={editFlowState}
                        className="lab-approvals-flow-icon--lg"
                      />
                      <h2>
                        {approvalModalSource === 'extract'
                          ? 'Review extracted data'
                          : 'Edit approval'}{' '}
                        — {form.workorder_id || selectedRow.workorder_id || '—'}
                      </h2>
                    </div>
                    <p
                      className={`lab-approvals-flow-badge lab-approvals-flow-badge--${editFlowState}`}
                    >
                      {LAB_FLOW_META[editFlowState].label}
                    </p>
                  </div>
                  <button type="button" className="close-button" onClick={closeEditModal}>
                    ×
                  </button>
                </div>

                <div
                  className={`lab-approvals-modal-split${
                    modalSplitOpen ? ' lab-approvals-modal-split--with-docusign' : ''
                  }`}
                >
                  <div className="lab-approvals-modal-split__form">
                <div className="modal-body">
                  {approvalModalSource === 'extract' &&
                    (extractModelLabel || extractPreviewUrl) && (
                      <>
                        {extractModelLabel ? (
                          <p className="modal-context extract-meta">{extractModelLabel}</p>
                        ) : null}
                        <div className="extract-preview-row">
                          {extractPreviewUrl ? (
                            extractPreviewUrl.startsWith('data:application/pdf') ? (
                              <embed
                                src={extractPreviewUrl}
                                type="application/pdf"
                                title="Uploaded invoice PDF preview"
                                className="extract-preview-img extract-preview-pdf"
                              />
                            ) : (
                              <img
                                src={extractPreviewUrl}
                                alt="Uploaded invoice preview"
                                className="extract-preview-img"
                              />
                            )
                          ) : null}
                          <p className="extract-instructions">
                            Confirm or edit fields below, then save into{' '}
                            <code className="lab-approvals-code">
                              als_approvals.workorder_approvals
                            </code>
                            .
                          </p>
                        </div>
                      </>
                    )}
                  <p className="modal-context">
                    {selectedRow.lab_name ? `${selectedRow.lab_name} · ` : ''}
                    ALS samples: {selectedRow.sample_count ?? '—'}
                  </p>
                  {(selectedRow.hole_ids?.length > 0 ||
                    selectedRow.news_release_dates?.length > 0 ||
                    selectedRow.qaqc_workorder_note) && (
                    <div className="lab-approvals-news-context">
                      {selectedRow.hole_ids?.length > 0 && (
                        <p className="modal-context modal-context--stacked">
                          <span className="lab-approvals-news-context__label">
                            News release holes
                          </span>
                          {selectedRow.hole_ids.join(', ')}
                        </p>
                      )}
                      {selectedRow.news_release_dates?.length > 0 && (
                        <p className="modal-context modal-context--stacked">
                          <span className="lab-approvals-news-context__label">
                            News release date(s)
                          </span>
                          {selectedRow.news_release_dates.join(', ')}
                        </p>
                      )}
                      {selectedRow.qaqc_workorder_note ? (
                        <p className="modal-context modal-context--stacked">
                          <span className="lab-approvals-news-context__label">
                            QAQC workorder note
                          </span>
                          {selectedRow.qaqc_workorder_note}
                        </p>
                      ) : null}
                    </div>
                  )}

                  <div className="approval-form-grid">
                    <div className="form-group form-group--full">
                      <label>Workorder ID (required)</label>
                      <input
                        type="text"
                        value={form.workorder_id}
                        onChange={(e) => handleFormChange('workorder_id', e.target.value)}
                        placeholder="e.g. WH26060118"
                        spellCheck={false}
                        autoComplete="off"
                        readOnly={approvalModalSource === 'list'}
                        title={
                          approvalModalSource === 'list'
                            ? 'From the workorder list'
                            : 'Edit if the invoice misread the id'
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>P.O No</label>
                      <input
                        type="text"
                        value={form.po_number}
                        onChange={(e) => handleFormChange('po_number', e.target.value)}
                        placeholder="Purchase order"
                      />
                    </div>
                    <div className="form-group">
                      <label>Sample Num prep</label>
                      <input
                        type="number"
                        min="0"
                        value={form.sample_num_prep}
                        onChange={(e) =>
                          handleFormChange('sample_num_prep', e.target.value)
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Sample Num analysis</label>
                      <input
                        type="number"
                        min="0"
                        value={form.sample_num_analysis}
                        onChange={(e) =>
                          handleFormChange('sample_num_analysis', e.target.value)
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Samples sent / shipped</label>
                      <input
                        type="number"
                        min="0"
                        value={form.samples_sent_shipped}
                        onChange={(e) =>
                          handleFormChange('samples_sent_shipped', e.target.value)
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Subtotal</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={form.subtotal}
                        onChange={(e) => handleFormChange('subtotal', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>GST</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={form.gst}
                        onChange={(e) => handleFormChange('gst', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Total</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={form.total}
                        onChange={(e) => handleFormChange('total', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Approved</label>
                      <select
                        className="status-filter"
                        value={form.approved}
                        onChange={(e) => handleFormChange('approved', e.target.value)}
                      >
                        <option value="">Not set</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Date</label>
                      <input
                        type="date"
                        value={form.approval_date}
                        onChange={(e) =>
                          handleFormChange('approval_date', e.target.value)
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Average sample cost</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={form.average_sample_cost}
                        onChange={(e) =>
                          handleFormChange('average_sample_cost', e.target.value)
                        }
                        onBlur={handleAverageSampleCostBlur}
                      />
                    </div>
                    <div className="form-group form-group--full lab-approvals-folio-row">
                      <label>Document folio (DocuSign envelope or Procurify bill uuid)</label>
                      <div className="lab-approvals-folio-row__inputs">
                        <input
                          type="text"
                          value={form.folio_id}
                          onChange={(e) => handleFormChange('folio_id', e.target.value)}
                          placeholder="DocuSign envelope GUID or Procurify bill uuid"
                          spellCheck={false}
                          autoComplete="off"
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={!(form.folio_id || '').trim() || actionInProgress}
                          onClick={() => {
                            if (formUsesProcurifySource(form, documentSource)) {
                              const folio = (form.folio_id || activeProcurifyBill?.uuid || '').trim();
                              procurifyLabService.openBillInProcurify(
                                folio || activeProcurifyBill?.id
                              );
                              openProcurifyBillInPanel(form.folio_id, {
                                id: form.procurify_bill_id || activeProcurifyBill?.id,
                                uuid: form.folio_id,
                                invoice_number: activeProcurifyBill?.invoice_number,
                                vendor_name: activeProcurifyBill?.vendor_name,
                              });
                              return;
                            }
                            openDocusignEnvelopeInNewTab(form.folio_id, {
                              withInvoiceExtract: false,
                              persistFolio: false,
                            });
                          }}
                        >
                          {formUsesProcurifySource(form, documentSource)
                            ? 'Open in Procurify'
                            : 'Open in DocuSign'}
                        </Button>
                      </div>
                      {(form.folio_id || docusignPanelEnvelopeId) &&
                      !formUsesProcurifySource(form, documentSource) ? (
                        <button
                          type="button"
                          className="lab-approvals-folio-row__other-wo"
                          disabled={envelopeSegmentPickerBusy || actionInProgress}
                          onClick={openOtherWorkordersInEnvelope}
                        >
                          Other workorders in this envelope
                        </button>
                      ) : null}
                    </div>
                  </div>

                  {computedAvgCost != null && Number.isFinite(computedAvgCost) && (
                    <p className="computed-hint">
                      From Total ÷ Sample Num analysis: {computedAvgCost.toFixed(2)}
                    </p>
                  )}

                  <div className="form-group form-group--full">
                    <label>Comments</label>
                    <textarea
                      value={form.comments}
                      onChange={(e) => handleFormChange('comments', e.target.value)}
                      rows={4}
                      placeholder="Notes or rejection reason"
                    />
                  </div>
                </div>
                  </div>

                  {showProcurifySidePanel ? (
                    <LabApprovalsProcurifyBillPanel
                      billId={
                        activeProcurifyBill?.uuid ||
                        activeProcurifyBill?.id ||
                        form.folio_id ||
                        form.procurify_bill_id
                      }
                      billPreview={activeProcurifyBill}
                      comment={form.comments}
                      onClose={closeProcurifyPanel}
                      onError={(msg) => setError(msg)}
                      onBillUpdated={() => {
                        setError(null);
                      }}
                    />
                  ) : null}
                  {docusignPanelUrl ? (
                    <aside
                      className="lab-approvals-modal-split__docusign"
                      aria-label="DocuSign signing"
                    >
                      <div className="lab-approvals-docusign-panel__toolbar">
                        <span className="lab-approvals-docusign-panel__label">DocuSign</span>
                        <div className="lab-approvals-docusign-panel__actions">
                          <a
                            className="lab-approvals-docusign-panel__link"
                            href={docusignPanelUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open in new tab
                          </a>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={closeDocusignPanel}
                          >
                            Close signing
                          </Button>
                        </div>
                      </div>
                      <p className="lab-approvals-docusign-panel__note">
                        If this stays blank, DocuSign may block iframes — use Open in new tab.
                      </p>
                      <iframe
                        ref={docusignIframeRef}
                        key={docusignPanelEnvelopeId || 'docusign'}
                        title="DocuSign"
                        src={docusignPanelUrl}
                        className="lab-approvals-docusign-panel__iframe lab-approvals-docusign-panel__iframe--modal"
                        referrerPolicy="no-referrer-when-downgrade"
                        onLoad={handleDocusignIframeLoad}
                      />
                    </aside>
                  ) : null}
                </div>

                <div className="modal-footer">
                  <Button
                    type="button"
                    onClick={closeEditModal}
                    variant="secondary"
                    disabled={actionInProgress}
                  >
                    Cancel
                  </Button>
                  {selectedRow.qaqc_passed === true && (
                    <Button
                      type="button"
                      variant="success"
                      onClick={handleSaveAndApprove}
                      disabled={actionInProgress || !workorderIdForSave()}
                    >
                      {actionInProgress ? 'Saving…' : 'Save to Database & Approve'}
                    </Button>
                  )}
                  <Button
                    type="button"
                    onClick={handleSaveApproval}
                    disabled={actionInProgress || !workorderIdForSave()}
                  >
                    {actionInProgress ? 'Saving…' : 'Save to database'}
                  </Button>
                </div>
              </Card>
            </div>
          </div>
          );
        })()}
      </div>

      {envelopeSegmentPicker?.multi &&
      envelopeSegmentPicker.segments?.length > 1 &&
      !showEditModal ? (
        <div
          className="lab-approvals-segment-picker-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lab-approvals-segment-picker-title"
          onClick={closeEnvelopeSegmentPicker}
        >
          <div
            className="lab-approvals-segment-picker-wrap"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
          <Card className="lab-approvals-segment-picker">
            <div className="lab-approvals-segment-picker__head">
              <div className="lab-approvals-segment-picker__title-row">
                <h2 id="lab-approvals-segment-picker-title">
                  Workorders in this envelope
                </h2>
                <button
                  type="button"
                  className="lab-approvals-segment-picker__close"
                  aria-label="Close workorder list"
                  disabled={envelopeSegmentPickerBusy}
                  onClick={closeEnvelopeSegmentPicker}
                >
                  ×
                </button>
              </div>
              <p className="lab-approvals-segment-picker__hint">
                This DocuSign envelope contains{' '}
                <strong>{envelopeSegmentPicker.segmentCount}</strong> invoices. Choose
                one to run AI extract and open the review form (same{' '}
                <code className="lab-approvals-code">folio_id</code> for all).
              </p>
            </div>
            <ul className="lab-approvals-segment-picker__list">
              {envelopeSegmentPicker.segments.map((seg) => {
                const label =
                  (seg.hint_workorder_id || '').trim() ||
                  (seg.name || '').trim() ||
                  `Segment ${(seg.segmentIndex ?? 0) + 1}`;
                const linked = resolveSegmentDashboardRow(
                  seg,
                  envelopeSegmentPicker.envelopeId,
                  approvalsByFolioWorkorder,
                  approvalsData
                );
                const linkBadge = linkedWorkorderBadge(linked);
                const flowState = linked
                  ? getLabApprovalFlowState(
                      linked.qaqc_passed,
                      linked.approval?.approved === true
                    )
                  : null;
                const alreadyApproved = flowState === 'approved';
                return (
                  <li key={`${seg.segmentIndex}-${seg.documentId}-${seg.page}`}>
                    <div className="lab-approvals-segment-picker__row">
                      <div className="lab-approvals-segment-picker__label">
                        <span className="lab-approvals-segment-picker__primary">{label}</span>
                        {seg.name && seg.hint_workorder_id && seg.name !== label ? (
                          <span className="lab-approvals-segment-picker__secondary">
                            {seg.name}
                          </span>
                        ) : null}
                      </div>
                      <div
                        className="lab-approvals-segment-picker__status-wrap"
                        title={linkBadge?.title || 'Not linked to a workorder yet'}
                      >
                        {flowState ? (
                          <LabApprovalFlowIcon state={flowState} />
                        ) : null}
                        {linkBadge ? (
                          <span
                            className={`lab-approvals-segment-picker__status lab-approvals-segment-picker__status--${linkBadge.key}`}
                          >
                            {linkBadge.label}
                          </span>
                        ) : (
                          <span className="lab-approvals-segment-picker__status lab-approvals-segment-picker__status--pending">
                            Not saved
                          </span>
                        )}
                      </div>
                      <div className="lab-approvals-segment-picker__actions">
                        <Button
                          type="button"
                          size="sm"
                          variant="success"
                          disabled={
                            envelopeSegmentPickerBusy ||
                            extractBusy ||
                            actionInProgress ||
                            alreadyApproved
                          }
                          title={
                            alreadyApproved
                              ? 'Already approved'
                              : 'AI extract, save, and approve when QAQC has passed'
                          }
                          onClick={() => handleEnvelopeSegmentSaveAndApprove(seg)}
                        >
                          Save & Approve
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          disabled={envelopeSegmentPickerBusy || extractBusy}
                          onClick={() => handleEnvelopeSegmentReview(seg)}
                        >
                          Review
                        </Button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="lab-approvals-segment-picker__footer">
              <Button
                type="button"
                variant="secondary"
                disabled={envelopeSegmentPickerBusy}
                onClick={closeEnvelopeSegmentPicker}
              >
                Close
              </Button>
            </div>
          </Card>
          </div>
        </div>
      ) : null}
      </div>
    </Layout>
  );
};

export default LabApprovals;
